/**
 * Password strength indicator component.
 * Shows visual feedback for password strength.
 */
import React from 'react';
import { calculatePasswordStrength, PasswordStrength } from '../utils/passwordStrength';
import './PasswordStrengthIndicator.css';

interface PasswordStrengthIndicatorProps {
  password: string;
  showLabel?: boolean;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  showLabel = true,
}) => {
  if (!password) {
    return null;
  }

  const { strength, checks } = calculatePasswordStrength(password);

  const strengthLabels: Record<PasswordStrength, string> = {
    weak: 'Weak',
    fair: 'Fair',
    good: 'Good',
    strong: 'Strong',
  };

  const strengthColors: Record<PasswordStrength, string> = {
    weak: 'var(--error-color)',
    fair: 'var(--warning-color)',
    good: 'var(--info-color)',
    strong: 'var(--success-color)',
  };

  return (
    <div className="password-strength-indicator">
      {showLabel && (
        <div className="password-strength-label">
          <span>Password Strength: </span>
          <span
            className="password-strength-value"
            style={{ color: strengthColors[strength] }}
          >
            {strengthLabels[strength]}
          </span>
        </div>
      )}
      <div className="password-strength-bars">
        {[1, 2, 3, 4].map((level) => {
          const isActive = level <= Object.values(checks).filter(Boolean).length;
          return (
            <div
              key={level}
              className={`password-strength-bar ${isActive ? 'active' : ''}`}
              style={{
                backgroundColor: isActive ? strengthColors[strength] : 'var(--border-color)',
              }}
            />
          );
        })}
      </div>
      <div className="password-strength-checks">
        <div className={`password-check ${checks.length ? 'check-pass' : 'check-fail'}`}>
          {checks.length ? '✓' : '✗'} At least 8 characters
        </div>
        <div className={`password-check ${checks.uppercase ? 'check-pass' : 'check-fail'}`}>
          {checks.uppercase ? '✓' : '✗'} One uppercase letter
        </div>
        <div className={`password-check ${checks.lowercase ? 'check-pass' : 'check-fail'}`}>
          {checks.lowercase ? '✓' : '✗'} One lowercase letter
        </div>
        <div className={`password-check ${checks.number ? 'check-pass' : 'check-fail'}`}>
          {checks.number ? '✓' : '✗'} One number
        </div>
        <div className={`password-check ${checks.special ? 'check-pass' : 'check-fail'}`}>
          {checks.special ? '✓' : '✗'} One special character
        </div>
      </div>
    </div>
  );
};

